import { useEffect, useState } from 'react'
import { KeyRound } from 'lucide-react'
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../lib/api'
import { useTenantId } from '../context/SessionContext'
import { useToast } from '../context/ToastContext'
import {
  IdPFormFields,
  buildIdPPayload,
  computeCallbackURIs,
  emptyIdPForm,
  type IdPFormState,
  PRESET_KEYS,
} from '../components/IdPForm'
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Modal,
  PageHeader,
  Skeleton,
  THead,
  Th,
  Td,
  Tr,
} from '../components/ui'

interface IdentityProvider {
  id: string
  name: string
  idp_type: string
  issuer_url: string
  client_id: string
  has_client_secret: boolean
  allowed_groups?: string[]
  is_default: boolean
}

interface DiscoveryResult {
  ok: boolean
  issuer?: string
  authorization_endpoint?: string
  token_endpoint?: string
  jwks_uri?: string
  error?: string
}

interface DirectoryMeta {
  scim_directory_enabled: boolean
  scim_onboarded_at?: string
  last_successful_scim_write_at?: string
  mapping_frozen: boolean
  scim_group_claim_attr: string
  scim_member_subject_attr: string
}

export default function AdminIdentityProviderPage() {
  const tenantId = useTenantId()
  const { toast } = useToast()
  const [providers, setProviders] = useState<IdentityProvider[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editID, setEditID] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [preset, setPreset] = useState<(typeof PRESET_KEYS)[number]>('custom')
  const [form, setForm] = useState<IdPFormState>(emptyIdPForm())
  const [verifying, setVerifying] = useState(false)
  const [verifyResult, setVerifyResult] = useState<DiscoveryResult | null>(null)
  const [dirMeta, setDirMeta] = useState<DirectoryMeta | null>(null)
  const [scimToken, setScimToken] = useState<string | null>(null)
  const [wouldFlip, setWouldFlip] = useState(false)
  const callbackURIs = computeCallbackURIs(typeof window !== 'undefined' ? window.location.host : '')

  const fetchAll = async () => {
    if (!tenantId) return
    setLoading(true)
    setError(null)
    try {
      const data = await apiGet<IdentityProvider[]>(`/api/v1/tenants/${tenantId}/idp`)
      setProviders(data ?? [])
      const st = await apiGet<{ first_onboard_would_flip: boolean }>(
        `/api/v1/tenants/${tenantId}/directory/status`,
      )
      setWouldFlip(!!st?.first_onboard_would_flip)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [tenantId])

  const loadDirectory = async (idpID: string) => {
    try {
      const meta = await apiGet<DirectoryMeta>(`/api/v1/tenants/${tenantId}/idp/${idpID}/directory`)
      setDirMeta(meta)
    } catch {
      setDirMeta(null)
    }
  }

  const openCreate = () => {
    setEditID(null)
    setForm(emptyIdPForm())
    setPreset('custom')
    setVerifyResult(null)
    setDirMeta(null)
    setScimToken(null)
    setShowForm(true)
  }

  const handleVerifyConfig = async () => {
    setVerifying(true)
    setVerifyResult(null)
    try {
      const payload = editID
        ? { idpID: editID, overrides: { issuer_url: form.issuer_url, client_id: form.client_id } }
        : buildIdPPayload(form)
      const result = await apiPost<DiscoveryResult>(`/api/v1/tenants/${tenantId}/idp/test`, payload)
      setVerifyResult(result)
    } catch (e: any) {
      setVerifyResult({ ok: false, error: e.message })
    } finally {
      setVerifying(false)
    }
  }

  const handleTestSignIn = () => {
    if (!editID) return
    window.open(`/auth/oidc/test/start?idpID=${encodeURIComponent(editID)}`, '_blank', 'noopener')
  }

  const handleTestRow = async (p: IdentityProvider) => {
    try {
      const result = await apiPost<DiscoveryResult>(`/api/v1/tenants/${tenantId}/idp/test`, { idpID: p.id })
      toast(
        result.ok
          ? { title: `${p.name}: configuration verified`, description: result.issuer, variant: 'success' }
          : { title: `${p.name}: verification failed`, description: result.error, variant: 'danger' },
      )
    } catch (e: any) {
      toast({ title: `${p.name}: verification failed`, description: e.message, variant: 'danger' })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    const payload = buildIdPPayload(form)
    try {
      if (editID) {
        await apiPut(`/api/v1/tenants/${tenantId}/idp/${editID}`, payload)
      } else {
        await apiPost(`/api/v1/tenants/${tenantId}/idp`, payload)
      }
      setForm(emptyIdPForm())
      setShowForm(false)
      setEditID(null)
      toast({ title: editID ? 'Identity provider updated' : 'Identity provider created', variant: 'success' })
      fetchAll()
    } catch (e: any) {
      setError(e.message)
      toast({ title: 'Save failed', description: e.message, variant: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (p: IdentityProvider) => {
    setForm({
      name: p.name,
      issuer_url: p.issuer_url,
      client_id: p.client_id,
      client_secret: '',
      allowed_groups: (p.allowed_groups ?? []).join(', '),
      is_default: p.is_default,
    })
    setPreset('custom')
    setEditID(p.id)
    setVerifyResult(null)
    setScimToken(null)
    setShowForm(true)
    loadDirectory(p.id)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this identity provider? Users on this tenant will be unable to sign in until another is configured.')) return
    try {
      await apiDelete(`/api/v1/tenants/${tenantId}/idp/${id}`)
      toast({ title: 'Identity provider deleted', variant: 'success' })
      fetchAll()
    } catch (e: any) {
      setError(e.message)
      toast({ title: 'Delete failed', description: e.message, variant: 'danger' })
    }
  }

  const mintScimToken = async () => {
    if (!editID) return
    const msg = wouldFlip
      ? `Onboarding SCIM for this IdP will stop live Authentik lookups tenant-wide. Groups from other IdPs will not appear in the picker or access-review until those IdPs are also SCIM-onboarded.\n\nContinue and mint token?`
      : 'Rotate SCIM token? The previous token is invalidated immediately. Update the IdP client and trigger Group re-push as one action.'
    if (!confirm(msg)) return
    try {
      const res = await apiPost<{ token: string }>(`/api/v1/tenants/${tenantId}/idp/${editID}/scim-token`, {
        acknowledge_mode_flip: true,
      })
      setScimToken(res.token)
      toast({ title: 'SCIM token minted', description: 'Copy it now. It is shown once.', variant: 'success' })
      loadDirectory(editID)
      fetchAll()
    } catch (e: any) {
      toast({ title: 'Mint failed', description: e.message, variant: 'danger' })
    }
  }

  const clearDirectory = async () => {
    if (!editID) return
    if (!confirm('Clear directory cache? This does not auto-refill. You must trigger an explicit Group re-push/resync in the IdP. Mapping will become editable again.')) return
    try {
      await apiPost(`/api/v1/tenants/${tenantId}/idp/${editID}/directory/clear`, { confirm: true })
      toast({ title: 'Directory cache cleared', variant: 'success' })
      setScimToken(null)
      loadDirectory(editID)
      fetchAll()
    } catch (e: any) {
      toast({ title: 'Clear failed', description: e.message, variant: 'danger' })
    }
  }

  const toggleDirectoryEnabled = async (enabled: boolean) => {
    if (!editID) return
    if (enabled && wouldFlip && !confirm(
      'Enabling SCIM directory for this IdP will stop live Authentik lookups tenant-wide once the first Group is pushed. Continue?',
    )) return
    try {
      await apiPatch(`/api/v1/tenants/${tenantId}/idp/${editID}/directory`, {
        scim_directory_enabled: enabled,
        acknowledge_mode_flip: true,
      })
      loadDirectory(editID)
    } catch (e: any) {
      toast({ title: 'Update failed', description: e.message, variant: 'danger' })
    }
  }

  return (
    <div>
      <PageHeader
        title="Identity Provider"
        description="Configure the identity provider used to authenticate your users."
        actions={<Button onClick={openCreate}>+ New Identity Provider</Button>}
      />

      {error && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-muted px-4 py-3 text-sm text-danger-foreground">
          {error}
        </div>
      )}

      {loading ? (
        <Skeleton className="h-48 w-full" />
      ) : providers.length === 0 ? (
        <EmptyState
          icon={<KeyRound className="h-8 w-8" />}
          title="No identity provider configured"
          description="Add your organization's OIDC provider so users can sign in."
          actionLabel="New Identity Provider"
          onAction={openCreate}
        />
      ) : (
        <DataTable>
          <THead>
            <Tr>
              <Th>Name</Th>
              <Th>Issuer</Th>
              <Th>Allowed Groups</Th>
              <Th className="text-right">Actions</Th>
            </Tr>
          </THead>
          <tbody>
            {providers.map((p) => (
              <Tr key={p.id}>
                <Td>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{p.name}</p>
                    {p.is_default && <Badge variant="primary">default</Badge>}
                    {!p.has_client_secret && <Badge variant="danger">no secret set</Badge>}
                  </div>
                  <p className="mt-0.5 text-xs text-muted">Client ID: {p.client_id}</p>
                </Td>
                <Td className="text-xs text-muted">{p.issuer_url}</Td>
                <Td className="text-xs text-muted">
                  {p.allowed_groups && p.allowed_groups.length > 0
                    ? p.allowed_groups.join(', ')
                    : 'Unrestricted'}
                </Td>
                <Td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleTestRow(p)}>
                      Test
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(p)}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" className="text-danger" onClick={() => handleDelete(p.id)}>
                      Delete
                    </Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      )}

      <Modal
        open={showForm}
        onClose={() => {
          setShowForm(false)
          setEditID(null)
        }}
        title={editID ? 'Edit Identity Provider' : 'Create Identity Provider'}
        className="max-w-2xl"
        footer={
          <>
            <Button
              variant="outline"
              onClick={handleVerifyConfig}
              disabled={verifying || !form.issuer_url || !form.client_id}
            >
              {verifying ? 'Verifying…' : 'Verify Configuration'}
            </Button>
            {editID && (
              <Button variant="outline" onClick={handleTestSignIn} title="Opens a real sign-in round trip in a new tab">
                Test Sign-In
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => {
                setShowForm(false)
                setEditID(null)
              }}
            >
              Cancel
            </Button>
            <Button type="submit" form="idp-form" disabled={saving}>
              {saving ? 'Saving…' : editID ? 'Save Changes' : 'Create Identity Provider'}
            </Button>
          </>
        }
      >
        <form id="idp-form" onSubmit={handleSubmit}>
          <p className="mb-4 text-xs text-muted">
            Register <code className="text-foreground">{callbackURIs.redirectURI}</code> as the callback URI in your
            provider's settings. Using Test Sign-In below also requires{' '}
            <code className="text-foreground">{callbackURIs.testRedirectURI}</code>.
          </p>
          {verifyResult && (
            <div
              className={`mb-4 rounded-lg border px-4 py-3 text-xs ${
                verifyResult.ok
                  ? 'border-success/30 bg-success-muted text-success-foreground'
                  : 'border-danger/30 bg-danger-muted text-danger-foreground'
              }`}
            >
              {verifyResult.ok ? (
                <>
                  Configuration verified. Discovery succeeded for <code>{verifyResult.issuer}</code>.
                </>
              ) : (
                <>Verification failed: {verifyResult.error}</>
              )}
            </div>
          )}
          <IdPFormFields
            form={form}
            setForm={setForm}
            editMode={!!editID}
            preset={preset}
            onPresetChange={setPreset}
          />

          {editID && dirMeta && (
            <div className="mt-6 space-y-3 border-t border-border pt-4">
              <h3 className="text-sm font-semibold">Group directory (SCIM)</h3>
              <p className="text-xs text-muted">
                Directory cache for access review and policy selection. Not used for authorization.
                Configure Groups-only / Group Push on the IdP (Users provisioning is not supported).
              </p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={dirMeta.scim_directory_enabled}
                  onChange={(e) => toggleDirectoryEnabled(e.target.checked)}
                />
                SCIM directory enabled
              </label>
              <p className="text-xs text-muted">
                Mapping: {dirMeta.scim_group_claim_attr} → claim_value; member{' '}
                {dirMeta.scim_member_subject_attr} → subject
                {dirMeta.mapping_frozen ? ' (frozen after onboard, clear cache to change)' : ''}
              </p>
              {dirMeta.scim_onboarded_at && (
                <p className="text-xs text-muted">Onboarded: {dirMeta.scim_onboarded_at}</p>
              )}
              {dirMeta.last_successful_scim_write_at && (
                <p className="text-xs text-muted">Last SCIM write: {dirMeta.last_successful_scim_write_at}</p>
              )}
              <p className="break-all text-xs font-mono text-muted">
                SCIM base: /scim/v2/tenants/{tenantId}/idp/{editID}/Groups
              </p>
              {scimToken && (
                <div className="break-all rounded-lg border border-border bg-surface-muted px-3 py-2 text-xs font-mono">
                  {scimToken}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={mintScimToken}>
                  Mint / rotate SCIM token
                </Button>
                <Button type="button" size="sm" variant="outline" className="text-danger" onClick={clearDirectory}>
                  Clear directory cache
                </Button>
              </div>
            </div>
          )}
        </form>
      </Modal>
    </div>
  )
}
